import {
  CASCADE_GENERATE_BLUEPRINT_ARTEFACTS,
  TOGGLE_SCREENING_CRITERIA_LOCK,
  UPDATE_SCREENING_CRITERIA,
} from "@lib/graphql/mutations";
import { ClipboardList, Loader2, Lock, Pencil, Sparkles, Unlock } from "lucide-react";
import { useState } from "react";
import { useMutation } from "urql";
import type { ScreeningCriteria } from "./briefTypes";
import { ScreeningCriteriaEditor } from "./ScreeningCriteriaEditor";

interface ScreeningCriteriaSectionProps {
  campaignId: string;
  screeningCriteria: ScreeningCriteria;
  blueprintLocked?: boolean;
  onSaved: () => void;
}

export function ScreeningCriteriaSection({
  campaignId,
  screeningCriteria,
  blueprintLocked = false,
  onSaved,
}: ScreeningCriteriaSectionProps) {
  const [editing, setEditing] = useState(false);
  const [{ fetching: saving }, updateCriteria] = useMutation(UPDATE_SCREENING_CRITERIA);
  const [{ fetching: toggling }, toggleLock] = useMutation(TOGGLE_SCREENING_CRITERIA_LOCK);
  const [{ fetching: regenerating }, cascadeGenerate] = useMutation(
    CASCADE_GENERATE_BLUEPRINT_ARTEFACTS,
  );

  const version = screeningCriteria.currentVersion;
  const locked = screeningCriteria.locked;
  const generating = version?.generationState === "generating";
  const regenerateDisabled = !blueprintLocked || generating || regenerating;
  const regenerateTitle = !blueprintLocked
    ? "Lock the blueprint first to regenerate from it"
    : generating
      ? "Generation already in progress"
      : "Regenerate from the locked blueprint";

  const handleRegenerate = async () => {
    if (regenerateDisabled) return;
    await cascadeGenerate({ input: { campaignId, kinds: ["screening_criteria"] } });
    onSaved();
  };

  const handleSave = async (
    mustHave: string[],
    niceToHave: string[],
    exclusions: string[],
    changeSummary: string | undefined,
  ) => {
    await updateCriteria({
      input: {
        campaignId,
        mustHaveCriteria: mustHave,
        niceToHaveCriteria: niceToHave,
        exclusions: exclusions,
        changeSummary,
      },
    });
    setEditing(false);
    onSaved();
  };

  const handleToggleLock = async () => {
    await toggleLock({ input: { campaignId } });
    onSaved();
  };

  if (editing) {
    return (
      <ScreeningCriteriaEditor
        initialMustHave={version?.mustHaveCriteria ?? []}
        initialNiceToHave={version?.niceToHaveCriteria ?? []}
        initialExclusions={version?.exclusions ?? []}
        onSave={handleSave}
        onCancel={() => setEditing(false)}
        saving={saving}
        requireChangeSummary={locked}
      />
    );
  }

  const mustHave = version?.mustHaveCriteria ?? [];
  const niceToHave = version?.niceToHaveCriteria ?? [];
  const exclusions = version?.exclusions ?? [];
  const empty = mustHave.length === 0 && niceToHave.length === 0 && exclusions.length === 0;

  return (
    <div id="screening-criteria" className="rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardList size={14} className="text-text-faint" />
          <h3 className="text-sm font-medium text-text-secondary">Screening Criteria</h3>
          {version && <span className="text-xs text-text-faint">v{version.versionNumber}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleRegenerate}
            disabled={regenerateDisabled}
            title={regenerateTitle}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            Regenerate from blueprint
          </button>
          <button
            type="button"
            onClick={handleToggleLock}
            disabled={toggling}
            title={locked ? "Unlock screening criteria" : "Lock screening criteria"}
            className={`flex items-center gap-1 rounded px-2 py-1 text-xs hover:bg-card-hover ${
              locked ? "text-amber-500" : "text-text-muted"
            }`}
          >
            {locked ? <Lock size={12} /> : <Unlock size={12} />}
            {locked ? "Locked" : "Lock"}
          </button>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
          >
            <Pencil size={12} /> Edit
          </button>
        </div>
      </div>

      {empty ? (
        <p className="text-sm text-text-muted">
          No screening criteria defined yet. Used by long-list creation and quick-assess flows to
          filter candidates against must-have, nice-to-have, and exclusion lists.
        </p>
      ) : (
        <div className="space-y-3 text-sm">
          {mustHave.length > 0 && (
            <CriteriaList label="Must have" items={mustHave} tone="primary" />
          )}
          {niceToHave.length > 0 && (
            <CriteriaList label="Nice to have" items={niceToHave} tone="muted" />
          )}
          {exclusions.length > 0 && (
            <CriteriaList label="Exclusions" items={exclusions} tone="rose" />
          )}
        </div>
      )}
    </div>
  );
}

interface CriteriaListProps {
  label: string;
  items: string[];
  tone: "primary" | "muted" | "rose";
}

function CriteriaList({ label, items, tone }: CriteriaListProps) {
  const dotClass =
    tone === "primary" ? "bg-accent" : tone === "rose" ? "bg-rose-500" : "bg-text-faint";
  return (
    <div>
      <span className="text-xs font-medium text-text-secondary">{label}</span>
      <ul className="mt-1 space-y-1">
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2 text-sm text-text-primary">
            <span className={`mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full ${dotClass}`} />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}
